import { useMemo } from 'react';
import type { AppointmentView } from '../api';
import { useI18n } from '../i18n';

/**
 * The schedule, drawn to scale.
 *
 * A list of appointments tells you what is booked. A calendar tells you what is
 * NOT — the forty minutes at eleven that could take an emergency, the afternoon
 * one chair is idle while the other is full. That gap is the thing a reception
 * desk actually needs to see, and a table cannot show it however well sorted.
 *
 * So appointments are positioned and sized by their real times. Three views,
 * each answering a different question:
 *
 *   DAY, in columns — one per dentist or one per chair. "Who is free at three."
 *   WEEK — seven days side by side. "When can she come back."
 *   MONTH — density per day. "How busy is next month."
 *
 * The grid is the CLINIC's day, not a fixed 00:00–24:00: showing fourteen empty
 * hours to make room for a practice that opens at nine wastes the space the
 * appointments need.
 */

export type CalendarView = 'day' | 'week' | 'month';

export interface Column {
  id: string;
  label: string;
}

/*
 * Tall enough that a 30-minute appointment can show a time and a name without
 * clipping either. Below about 1.3 the two lines stop fitting, and a booking
 * you cannot read is not much better than a booking you cannot see.
 */
const PX_PER_MINUTE = 1.45;

export function DayGrid({
  appointments,
  columns,
  columnOf,
  dayStartHour,
  dayEndHour,
  onOpen,
}: {
  appointments: AppointmentView[];
  columns: Column[];
  /** Which column an appointment belongs in. Null = it belongs in none. */
  columnOf: (a: AppointmentView) => string | null;
  dayStartHour: number;
  dayEndHour: number;
  onOpen: (a: AppointmentView) => void;
}) {
  const { formatTime, locale } = useI18n();
  const totalMinutes = (dayEndHour - dayStartHour) * 60;
  const height = totalMinutes * PX_PER_MINUTE;

  const hours = useMemo(
    () => Array.from({ length: dayEndHour - dayStartHour + 1 }, (_, i) => dayStartHour + i),
    [dayStartHour, dayEndHour],
  );

  return (
    <div className="cal-day" style={{ ['--cal-height' as string]: `${height}px` }}>
      <div className="cal-gutter">
        {hours.map((h) => (
          <div key={h} className="cal-hour-label" style={{ top: (h - dayStartHour) * 60 * PX_PER_MINUTE }}>
            <span className="ltr-value">{String(h).padStart(2, '0')}:00</span>
          </div>
        ))}
      </div>

      <div className="cal-columns" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(150px, 1fr))` }}>
        {columns.map((col) => {
          const mine = appointments.filter((a) => columnOf(a) === col.id);
          return (
            <div key={col.id} className="cal-column">
              <div className="cal-column-head" title={col.label}>
                {col.label}
              </div>
              <div className="cal-track" style={{ height }}>
                {hours.map((h) => (
                  <div
                    key={h}
                    className="cal-rule"
                    style={{ top: (h - dayStartHour) * 60 * PX_PER_MINUTE }}
                    aria-hidden="true"
                  />
                ))}
                {mine.map((a) => {
                  const geometry = place(a, dayStartHour, totalMinutes);
                  if (!geometry) return null;
                  // Appointments that overlap within one column are narrowed and
                  // offset so neither is hidden behind the other.
                  const lanes = overlapping(mine, a, dayStartHour, totalMinutes);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      className={`cal-event ${tone(a.status, a)}`}
                      style={{
                        top: geometry.top,
                        height: Math.max(geometry.height, 22),
                        insetInlineStart: `${(lanes.index / lanes.count) * 100}%`,
                        width: `calc(${100 / lanes.count}% - 4px)`,
                      }}
                      onClick={() => onOpen(a)}
                      title={`${formatTime(a.startsAt)}–${formatTime(a.endsAt)} · ${a.dentistName}`}
                    >
                      {/* A short block shows the time beside the name rather
                          than above it; there is only room for one line. */}
                      {geometry.height < 40 ? (
                        <span className="cal-event-title">
                          <span className="ltr-value" style={{ opacity: 0.75 }}>
                            {formatTime(a.startsAt)}
                          </span>{' '}
                          {label(a, locale)}
                        </span>
                      ) : (
                        <>
                          <span className="cal-event-time ltr-value">{formatTime(a.startsAt)}</span>
                          <span className="cal-event-title">{label(a, locale)}</span>
                          {a.reason && geometry.height > 62 ? (
                            <span className="cal-event-sub">{a.reason}</span>
                          ) : null}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeekGrid({
  appointments,
  days,
  dayStartHour,
  dayEndHour,
  onOpen,
}: {
  appointments: AppointmentView[];
  days: string[];
  dayStartHour: number;
  dayEndHour: number;
  onOpen: (a: AppointmentView) => void;
}) {
  const { formatDate } = useI18n();
  return (
    <DayGrid
      appointments={appointments}
      columns={days.map((d) => ({ id: d, label: formatDate(`${d}T12:00:00`) }))}
      columnOf={(a) => localDay(a.startsAt)}
      dayStartHour={dayStartHour}
      dayEndHour={dayEndHour}
      onOpen={onOpen}
    />
  );
}

/**
 * A month, as density.
 *
 * Deliberately not to scale — at this zoom nobody is reading a 30-minute slot,
 * they are looking for the week that is already full. Each day shows its count
 * and its first few bookings, and says how many more there are rather than
 * silently cutting the list off.
 */
export function MonthGrid({
  appointments,
  month,
  onPickDay,
  onOpen,
}: {
  appointments: AppointmentView[];
  /** YYYY-MM. */
  month: string;
  onPickDay: (day: string) => void;
  onOpen: (a: AppointmentView) => void;
}) {
  const { t, formatTime, locale } = useI18n();
  const cells = useMemo(() => monthCells(month), [month]);
  const byDay = useMemo(() => {
    const map = new Map<string, AppointmentView[]>();
    for (const a of appointments) {
      const day = localDay(a.startsAt);
      const list = map.get(day) ?? [];
      list.push(a);
      map.set(day, list);
    }
    for (const list of map.values()) list.sort((x, y) => x.startsAt.localeCompare(y.startsAt));
    return map;
  }, [appointments]);

  const weekdays = new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en-GB', { weekday: 'short' });
  const today = localDay(new Date().toISOString());

  return (
    <div className="cal-month">
      {cells.slice(0, 7).map((c) => (
        <div key={`h-${c.day}`} className="cal-month-head">
          {weekdays.format(new Date(`${c.day}T12:00:00`))}
        </div>
      ))}
      {cells.map((c) => {
        const list = byDay.get(c.day) ?? [];
        return (
          <button
            key={c.day}
            type="button"
            className={`cal-month-cell${c.inMonth ? '' : ' outside'}${c.day === today ? ' today' : ''}`}
            onClick={() => onPickDay(c.day)}
          >
            <span className="cal-month-date ltr-value">{Number(c.day.slice(8))}</span>
            {list.length > 0 ? <span className="cal-month-count">{list.length}</span> : null}
            <span className="cal-month-list">
              {list.slice(0, 3).map((a) => (
                <span
                  key={a.id}
                  className={`cal-month-item ${tone(a.status, a)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(a);
                  }}
                >
                  <span className="ltr-value">{formatTime(a.startsAt)}</span> {label(a, locale)}
                </span>
              ))}
              {list.length > 3 ? (
                <span className="cal-month-more">
                  +{list.length - 3} {t.appointments.more}
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ helpers

function label(a: AppointmentView, locale: string): string {
  if (a.patient) {
    // In Arabic, the Arabic name when the record has one (as the appointment window shows).
    const ar = locale === 'ar' && a.patient.givenNameAlt;
    const first = ar ? a.patient.givenNameAlt! : a.patient.givenName;
    const last = ar ? (a.patient.familyNameAlt ?? '') : a.patient.familyName;
    return `${first} ${last}`.trim();
  }
  // A public request has no patient record yet — it has a name someone typed.
  return a.reason ?? '—';
}

function tone(status: string, a?: AppointmentView): string {
  // A booking accepted on top of another while a device was offline: red
  // until reception moves it (ADR-0009).
  if (a && a.provisional && a.overlapNotedAt && status !== 'cancelled') return 'is-overlap';
  if (status === 'cancelled') return 'is-cancelled';
  if (status === 'no_show') return 'is-noshow';
  if (status === 'completed') return 'is-done';
  if (status === 'in_treatment' || status === 'checked_in') return 'is-active';
  if (status === 'confirmed') return 'is-confirmed';
  return '';
}

/** Local calendar day of an instant, in the reader's own zone. */
function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function minutesFromStart(iso: string, dayStartHour: number): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes() - dayStartHour * 60;
}

function place(
  a: AppointmentView,
  dayStartHour: number,
  totalMinutes: number,
): { top: number; height: number } | null {
  const start = minutesFromStart(a.startsAt, dayStartHour);
  const end = minutesFromStart(a.endsAt, dayStartHour);
  // Clamped rather than dropped: an appointment that runs past closing should
  // still be visible, pinned to the edge of the grid.
  if (end <= 0 || start >= totalMinutes) return null;
  const top = Math.max(0, start) * PX_PER_MINUTE;
  const height = (Math.min(end, totalMinutes) - Math.max(0, start)) * PX_PER_MINUTE;
  return { top, height };
}

/**
 * Where this appointment sits among the ones it overlaps.
 *
 * Simple lane packing: everything that overlaps shares the column's width and
 * each takes the first free lane. Enough for a clinic, where genuine overlap in
 * one column means a double-booking somebody needs to SEE rather than a layout
 * problem to solve elegantly.
 */
function overlapping(
  all: AppointmentView[],
  a: AppointmentView,
  dayStartHour: number,
  totalMinutes: number,
): { index: number; count: number } {
  const mine = place(a, dayStartHour, totalMinutes);
  if (!mine) return { index: 0, count: 1 };

  const clashing = all.filter((other) => {
    const geometry = place(other, dayStartHour, totalMinutes);
    if (!geometry) return false;
    return geometry.top < mine.top + mine.height && mine.top < geometry.top + geometry.height;
  });
  clashing.sort((x, y) => x.startsAt.localeCompare(y.startsAt) || x.id.localeCompare(y.id));
  return { index: Math.max(0, clashing.findIndex((x) => x.id === a.id)), count: Math.max(1, clashing.length) };
}

/** Six weeks of cells covering the month, starting on Sunday. */
function monthCells(month: string): { day: string; inMonth: boolean }[] {
  const year = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const first = new Date(year, m - 1, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return {
      day: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      inMonth: d.getMonth() === m - 1,
    };
  });
}

/**
 * The hours worth drawing.
 *
 * Derived from what is actually booked, padded by an hour either side, and
 * never narrower than a normal working day — so an empty calendar still looks
 * like a day rather than a sliver, and a late emergency at 21:00 extends the
 * grid instead of falling off it.
 */
export function dayBounds(appointments: AppointmentView[]): { start: number; end: number } {
  let start = 8;
  let end = 19;
  for (const a of appointments) {
    const from = new Date(a.startsAt).getHours();
    const to = new Date(a.endsAt);
    const until = to.getHours() + (to.getMinutes() > 0 ? 1 : 0);
    if (from < start) start = Math.max(0, from - 1);
    if (until > end) end = Math.min(24, until + 1);
  }
  return { start, end };
}
